import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import authService from "../../services/authService";
import { applyGlobalPermissionOverrides } from "../../utils/permissions";

// Async thunk to refresh user permissions
export const refreshPermissions = createAsyncThunk(
  'auth/refreshPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getUserPermissions();
      if (response.success) {
        return response.data.permissions;
      } else {
        return rejectWithValue('Failed to fetch permissions');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh permissions');
    }
  }
);

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roles?: string[]; // RBAC roles array
  phone?: string;
  department?: string;
  employee_id?: string;
  avatar?: string;
  permissions?: Record<string, Record<string, boolean>>;
  branch_id?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isPasswordResetRequested: boolean;
  passwordResetError: string | null;
  isPasswordChangeRequested: boolean;
  passwordChangeError: string | null;
}

// Check for existing token and user data in localStorage
const token = localStorage.getItem("token");
let savedUser = null;

try {
  const userData = localStorage.getItem("user");
  if (userData && userData !== "undefined" && userData !== "null") {
    const parsedUser = JSON.parse(userData);
    // Transform snake_case to camelCase if needed
    savedUser = {
      ...parsedUser,
      firstName: parsedUser.firstName || parsedUser.first_name || "",
      lastName: parsedUser.lastName || parsedUser.last_name || "",
    };
  }
} catch (error) {
  console.error("Error parsing user data from localStorage:", error);
  localStorage.removeItem("user");
}

console.log("savedUser", savedUser);
console.log("token", token);

const initialState: AuthState = {
  user: savedUser,
  token: token,
  refreshToken: localStorage.getItem("refreshToken"),
  isAuthenticated: false, // Start as false, will be set by auth initialization
  isLoading: false,
  error: null,
  isPasswordResetRequested: false,
  passwordResetError: null,
  isPasswordChangeRequested: false,
  passwordChangeError: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ user: User; token: string; refreshToken: string }>
    ) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isLoading = false;
      state.error = null;
      // Save user data and tokens to localStorage
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("refreshToken", action.payload.refreshToken);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = action.payload;
      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isLoading = false;
      state.error = null;
      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    forgotPasswordStart: (state) => {
      state.isLoading = true;
      state.passwordResetError = null;
    },
    forgotPasswordSuccess: (state) => {
      state.isLoading = false;
      state.isPasswordResetRequested = true;
      state.passwordResetError = null;
    },
    forgotPasswordFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.passwordResetError = action.payload;
    },
    resetPasswordStart: (state) => {
      state.isLoading = true;
      state.passwordResetError = null;
    },
    resetPasswordSuccess: (state) => {
      state.isLoading = false;
      state.isPasswordResetRequested = false;
      state.passwordResetError = null;
    },
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.passwordResetError = action.payload;
    },
    changePasswordStart: (state) => {
      state.isLoading = true;
      state.passwordChangeError = null;
    },
    changePasswordSuccess: (state) => {
      state.isLoading = false;
      state.isPasswordChangeRequested = false;
      state.passwordChangeError = null;
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.passwordChangeError = action.payload;
    },
    setPermissions: (state, action: PayloadAction<Record<string, Record<string, boolean>>>) => {
      if (state.user) {
        state.user.permissions = action.payload;
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    restoreAuth: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.permissions = applyGlobalPermissionOverrides(action.payload);
          // Update localStorage
          localStorage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(refreshPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  clearError,
  forgotPasswordStart,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFailure,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  setPermissions,
  restoreAuth,
} = authSlice.actions;
export default authSlice.reducer;
